/**
 * Share a meeting series with an email that isn't on the calendar invite.
 *
 * Two modes captured in one checkbox: "Send recap + pre-meeting nudge emails" (default on)
 * controls `meeting_series_shares.notify_via_email`. Either way the shared user gets in-app
 * view access via the attendee gate (magic link). Owner + workspace admin can manage shares.
 *
 * For 1-on-1 meetings we surface a confirm step before adding because the content is more
 * sensitive than a team meeting - the friction is intentional, not enforced in RLS.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@saas-boilerplate/shared';
import { useAuth } from '@/contexts/auth-context';
import { toast } from 'sonner';
import { Loader2, Mail, Trash2, UserPlus, AlertTriangle, Share2, CalendarRange, Calendar as CalendarIcon, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { invokeEdgeFn } from '@/lib/edge-functions';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import {
  RadioGroup,
  RadioGroupItem,
} from './ui/radio-group';

type ShareRow = {
  id: string;
  shared_with_email: string;
  notify_via_email: boolean;
  added_by_user_id: string | null;
  created_at: string;
  meeting_id: string | null;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  seriesId: string;
  workspaceId: string;
  seriesTitle: string;
  /** Meeting type, e.g. 'one_on_one' - drives the privacy confirm dialog. */
  meetingType?: string | null;
  /** Currently-focused meeting (occurrence). When set, the dialog offers a per-occurrence scope. */
  currentMeetingId?: string | null;
  /** Display label for the current occurrence, e.g. "Wed 3 Jun, 15:00". */
  currentMeetingLabel?: string | null;
  /** Prefilled, fully-editable plain-text body for the "Email notes & decisions" option. */
  summaryDraft?: string;
  /** Title of the current meeting - seeds the email subject. */
  currentMeetingTitle?: string | null;
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function ShareSeriesDialog({
  open,
  onOpenChange,
  seriesId,
  workspaceId,
  seriesTitle,
  meetingType,
  currentMeetingId,
  currentMeetingLabel,
  summaryDraft,
  currentMeetingTitle,
}: Props) {
  const { user } = useAuth();
  const [shares, setShares] = useState<ShareRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [notifyByEmail, setNotifyByEmail] = useState(true);
  /** 'series' shares all future occurrences; 'occurrence' shares just one meeting;
   *  'email_summary' sends a one-off email of the notes/decisions/actions (no access granted). */
  const [scope, setScope] = useState<'series' | 'occurrence' | 'email_summary'>('series');
  /** Editable subject + body for the email_summary option. */
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [sendingSummary, setSendingSummary] = useState(false);
  const [adding, setAdding] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  /** When set, blocks Add and surfaces the 1-on-1 privacy confirm before insertion. */
  const [confirmOneOnOne, setConfirmOneOnOne] = useState<
    { email: string; notify: boolean; scope: 'series' | 'occurrence' } | null
  >(null);

  const isOneOnOne = meetingType === 'one_on_one';
  const canShareOccurrence = Boolean(currentMeetingId);

  const loadShares = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('meeting_series_shares')
        .select('id, shared_with_email, notify_via_email, added_by_user_id, created_at, meeting_id')
        .eq('series_id', seriesId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      setShares((data as ShareRow[]) || []);
    } catch (err: any) {
      toast.error('Failed to load shares', { description: err?.message });
    } finally {
      setLoading(false);
    }
  }, [seriesId]);

  /** Group existing shares so the list can render series vs per-occurrence sections distinctly. */
  const grouped = useMemo(() => {
    const series = shares.filter((s) => !s.meeting_id);
    const occurrence = shares.filter((s) => s.meeting_id);
    return { series, occurrence };
  }, [shares]);

  useEffect(() => {
    if (!open) return;
    void loadShares();
    // Seed the editable email fields from the current meeting's content each time we open.
    setEmailSubject(currentMeetingTitle ? `Notes from ${currentMeetingTitle}` : 'Meeting notes');
    setEmailBody(summaryDraft || '');
  }, [open, loadShares, currentMeetingTitle, summaryDraft]);

  // Reset transient state when the dialog closes so reopening starts fresh.
  useEffect(() => {
    if (open) return;
    setEmailInput('');
    setNotifyByEmail(true);
    setScope('series');
    setConfirmOneOnOne(null);
    setEmailSubject('');
    setEmailBody('');
  }, [open]);

  const performAdd = async (email: string, notify: boolean, scopeChoice: 'series' | 'occurrence') => {
    if (!user) return;
    setAdding(true);
    try {
      const normalized = email.trim().toLowerCase();
      const meetingIdForScope = scopeChoice === 'occurrence' ? currentMeetingId || null : null;
      const { data: inserted, error } = await supabase
        .from('meeting_series_shares')
        .insert({
          workspace_id: workspaceId,
          series_id: seriesId,
          meeting_id: meetingIdForScope,
          shared_with_email: normalized,
          notify_via_email: notify,
          added_by_user_id: user.id,
        })
        .select('id')
        .maybeSingle();
      if (error) {
        // Friendly message for the unique-constraint violation (already shared at this scope).
        if (String(error.code) === '23505' || /duplicate/i.test(error.message || '')) {
          toast.info('Already shared with that email at this scope', {
            description: 'Update the email preference inline if you want to change it.',
          });
        } else {
          throw error;
        }
      } else {
        const insertedId = (inserted as { id?: string } | null)?.id || null;
        toast.success(
          notify
            ? scopeChoice === 'occurrence'
              ? 'Shared - they’ll get the recap and pre-meeting nudge for that one meeting'
              : 'Shared - they’ll get recap + nudge emails'
            : scopeChoice === 'occurrence'
              ? 'Shared - view-only for that one meeting'
              : 'Shared - view-only',
        );
        setEmailInput('');
        setNotifyByEmail(true);
        setScope('series');
        await loadShares();

        // Fire the welcome email (link-only). View-only shares stay silent - the edge
        // function double-checks notify_via_email and skips otherwise.
        if (notify && insertedId) {
          try {
            await invokeEdgeFn('share-welcome-email', { share_id: insertedId });
          } catch (err: any) {
            // Non-fatal: the share is recorded, the recipient just didn't get the welcome
            // ping. Surface as a soft warning rather than an error.
            toast.warning('Shared - welcome email failed to send', {
              description: err?.message || 'They still have access; they’ll see the next recap.',
            });
          }
        }
      }
    } catch (err: any) {
      toast.error('Failed to share', { description: err?.message });
    } finally {
      setAdding(false);
      setConfirmOneOnOne(null);
    }
  };

  const handleAddClick = () => {
    const email = emailInput.trim().toLowerCase();
    if (!email) {
      toast.error('Enter an email address');
      return;
    }
    if (!EMAIL_RE.test(email)) {
      toast.error('That doesn’t look like a valid email');
      return;
    }
    const effectiveScope = scope === 'occurrence' && canShareOccurrence ? 'occurrence' : 'series';
    // Dup check accounts for scope: a series-level share blocks adding *anything* for that
    // email (since series-level already covers all occurrences); a per-occurrence share only
    // blocks another row at the same scope.
    const dup = shares.find((s) => {
      if (s.shared_with_email !== email) return false;
      if (!s.meeting_id) return true; // any new add for this email is redundant
      return effectiveScope === 'occurrence' && s.meeting_id === currentMeetingId;
    });
    if (dup) {
      toast.info('Already shared with that email at this scope');
      return;
    }
    if (isOneOnOne) {
      setConfirmOneOnOne({ email, notify: notifyByEmail, scope: effectiveScope });
      return;
    }
    void performAdd(email, notifyByEmail, effectiveScope);
  };

  /** Email-summary option: pipe the (edited) notes/decisions/actions through Resend. Sends a plain
   *  email only - grants no access to the meeting, adds no share row. */
  const handleSendSummary = async () => {
    const to = emailInput.trim().toLowerCase();
    if (!to || !EMAIL_RE.test(to)) {
      toast.error('Enter a valid recipient email');
      return;
    }
    if (!emailBody.trim()) {
      toast.error('Nothing to send - the email body is empty');
      return;
    }
    if (!currentMeetingId) {
      toast.error('No meeting in focus to summarise');
      return;
    }
    setSendingSummary(true);
    try {
      await invokeEdgeFn('share-meeting-summary-email', {
        meeting_id: currentMeetingId,
        workspace_id: workspaceId,
        to,
        subject: emailSubject.trim() || 'Meeting notes',
        body: emailBody,
      });
      toast.success(`Notes emailed to ${to}`);
      setEmailInput('');
      onOpenChange(false);
    } catch (err: any) {
      toast.error('Failed to send email', { description: err?.message });
    } finally {
      setSendingSummary(false);
    }
  };

  const handleToggleNotify = async (row: ShareRow, notify: boolean) => {
    try {
      const { error } = await supabase
        .from('meeting_series_shares')
        .update({ notify_via_email: notify })
        .eq('id', row.id);
      if (error) throw error;
      setShares((prev) => prev.map((s) => (s.id === row.id ? { ...s, notify_via_email: notify } : s)));
    } catch (err: any) {
      toast.error('Failed to update', { description: err?.message });
    }
  };

  const handleRevoke = async (row: ShareRow) => {
    setRevokingId(row.id);
    try {
      const { error } = await supabase.from('meeting_series_shares').delete().eq('id', row.id);
      if (error) throw error;
      setShares((prev) => prev.filter((s) => s.id !== row.id));
      toast.success(`Removed ${row.shared_with_email}`);
    } catch (err: any) {
      toast.error('Failed to remove', { description: err?.message });
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className={
            scope === 'email_summary'
              ? 'w-[80vw] max-w-[80vw] sm:max-w-[80vw]'
              : 'sm:max-w-[520px]'
          }
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base font-semibold">
              <Share2 className="h-4 w-4 text-primary" />
              Share &ldquo;{seriesTitle}&rdquo;
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Give someone access to this series without adding them to the calendar invite. They
              sign in via a magic link to their email.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            {/* Add form */}
            <div className="space-y-3 rounded-lg border border-border/50 bg-muted/20 p-4">
              <div className="space-y-2">
                <Label htmlFor="share-email" className="text-xs font-medium">
                  {scope === 'email_summary' ? 'Send to' : 'Email address'}
                </Label>
                <Input
                  id="share-email"
                  type="email"
                  inputMode="email"
                  placeholder="manager@company.com"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  onKeyDown={(e) =>
                    e.key === 'Enter' && (scope === 'email_summary' ? undefined : handleAddClick())
                  }
                  disabled={adding || sendingSummary}
                />
              </div>

              {/* Scope: series vs single occurrence. Only offered when we know which occurrence
                   is in focus (currentMeetingId). */}
              {canShareOccurrence && (
                <div className="space-y-2">
                  <Label className="text-xs font-medium">Scope</Label>
                  <RadioGroup
                    value={scope}
                    onValueChange={(v) => setScope(v as 'series' | 'occurrence')}
                    className="flex flex-col gap-1.5"
                  >
                    <label className="flex cursor-pointer items-start gap-2 rounded-md border border-transparent px-1 py-1 text-xs hover:border-border/40">
                      <RadioGroupItem value="series" className="mt-0.5" />
                      <span>
                        <span className="font-medium text-foreground">Whole series</span>
                        <span className="ml-1 text-muted-foreground">
                          - past meetings plus every future occurrence
                        </span>
                      </span>
                    </label>
                    <label className="flex cursor-pointer items-start gap-2 rounded-md border border-transparent px-1 py-1 text-xs hover:border-border/40">
                      <RadioGroupItem value="occurrence" className="mt-0.5" />
                      <span>
                        <span className="font-medium text-foreground">Just this meeting</span>
                        {currentMeetingLabel && (
                          <span className="ml-1 text-muted-foreground">- {currentMeetingLabel}</span>
                        )}
                        <span className="ml-1 text-muted-foreground">
                          (one recap + one pre-meeting nudge)
                        </span>
                      </span>
                    </label>
                    <label className="flex cursor-pointer items-start gap-2 rounded-md border border-transparent px-1 py-1 text-xs hover:border-border/40">
                      <RadioGroupItem value="email_summary" className="mt-0.5" />
                      <span>
                        <span className="font-medium text-foreground">Email notes &amp; decisions</span>
                        <span className="ml-1 text-muted-foreground">
                          - a one-off email of the notes, decisions &amp; action items (no meeting access)
                        </span>
                      </span>
                    </label>
                  </RadioGroup>
                </div>
              )}

              {scope === 'email_summary' ? (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="share-subject" className="text-xs font-medium">
                      Subject
                    </Label>
                    <Input
                      id="share-subject"
                      value={emailSubject}
                      onChange={(e) => setEmailSubject(e.target.value)}
                      disabled={sendingSummary}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="share-body" className="text-xs font-medium">
                      Message
                    </Label>
                    <Textarea
                      id="share-body"
                      value={emailBody}
                      onChange={(e) => setEmailBody(e.target.value)}
                      rows={18}
                      disabled={sendingSummary}
                      className="max-h-[55vh] font-mono text-xs leading-relaxed"
                    />
                    <p className="text-[11px] text-muted-foreground">
                      Edit freely before sending. A subtle &ldquo;Sent via Upstand&rdquo; line is added at
                      the bottom. This emails the text only - it does <span className="font-medium">not</span>{' '}
                      give the recipient access to the meeting.
                    </p>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleSendSummary}
                    disabled={sendingSummary || !emailInput.trim() || !emailBody.trim()}
                    className="gap-1.5"
                  >
                    {sendingSummary ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Send className="h-3.5 w-3.5" />
                    )}
                    Send email
                  </Button>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="share-notify"
                      checked={notifyByEmail}
                      onCheckedChange={(v) => setNotifyByEmail(v === true)}
                      disabled={adding}
                    />
                    <Label htmlFor="share-notify" className="cursor-pointer text-xs font-normal">
                      Send recap and pre-meeting nudge emails
                    </Label>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleAddClick}
                    disabled={adding || !emailInput.trim()}
                    className="gap-1.5"
                  >
                    {adding ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <UserPlus className="h-3.5 w-3.5" />
                    )}
                    Add
                  </Button>
                </>
              )}
            </div>

            {/* Current shares - link-based access only; not relevant when emailing a summary. */}
            {scope !== 'email_summary' && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Shared with
                </h4>
                {loading && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
              </div>

              {!loading && shares.length === 0 && (
                <p className="rounded-md border border-dashed border-border/60 bg-muted/10 px-3 py-3 text-xs italic text-muted-foreground">
                  Not shared with anyone yet.
                </p>
              )}

              {shares.length > 0 && (
                <div className="space-y-3">
                  {grouped.series.length > 0 && (
                    <ShareGroup
                      icon={<CalendarRange className="h-3 w-3" />}
                      label="Whole series"
                      rows={grouped.series}
                      onToggleNotify={handleToggleNotify}
                      onRevoke={handleRevoke}
                      revokingId={revokingId}
                    />
                  )}
                  {grouped.occurrence.length > 0 && (
                    <ShareGroup
                      icon={<CalendarIcon className="h-3 w-3" />}
                      label="Single occurrence"
                      rows={grouped.occurrence}
                      onToggleNotify={handleToggleNotify}
                      onRevoke={handleRevoke}
                      revokingId={revokingId}
                      showMeetingHint={(row) =>
                        row.meeting_id === currentMeetingId ? currentMeetingLabel ?? null : null
                      }
                    />
                  )}
                </div>
              )}
            </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 1-on-1 privacy confirm - friction only, not enforced server-side. */}
      <AlertDialog
        open={confirmOneOnOne != null}
        onOpenChange={(o) => !o && setConfirmOneOnOne(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              Share a 1-on-1?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This is a private 1-on-1 series. Sharing with{' '}
              <span className="font-medium">{confirmOneOnOne?.email}</span> will give them access to{' '}
              {confirmOneOnOne?.scope === 'occurrence' && currentMeetingLabel
                ? `the ${currentMeetingLabel} recap, decisions, and action items.`
                : 'every recap, decision, and action item - past and future.'}{' '}
              Continue only if both participants are aware.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={adding}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmOneOnOne) {
                  void performAdd(confirmOneOnOne.email, confirmOneOnOne.notify, confirmOneOnOne.scope);
                }
              }}
              disabled={adding}
            >
              {adding ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
              Yes, share
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function ShareGroup({
  icon,
  label,
  rows,
  onToggleNotify,
  onRevoke,
  revokingId,
  showMeetingHint,
}: {
  icon: React.ReactNode;
  label: string;
  rows: ShareRow[];
  onToggleNotify: (row: ShareRow, notify: boolean) => void | Promise<void>;
  onRevoke: (row: ShareRow) => void | Promise<void>;
  revokingId: string | null;
  /** Optional callback to surface a per-row meeting label (e.g. "Wed 3 Jun, 15:00"). */
  showMeetingHint?: (row: ShareRow) => string | null;
}) {
  return (
    <div className="overflow-hidden rounded-md border border-border/50">
      <div className="flex items-center gap-1.5 border-b border-border/40 bg-muted/30 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {icon}
        {label}
      </div>
      <ul className="divide-y divide-border/40">
        {rows.map((s) => {
          const hint = showMeetingHint?.(s) ?? null;
          return (
            <li key={s.id} className="flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2.5">
              <Mail className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1 truncate text-sm">
                {s.shared_with_email}
                {hint && (
                  <span className="ml-1 text-[11px] text-muted-foreground">({hint})</span>
                )}
              </span>
              <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                <Checkbox
                  checked={s.notify_via_email}
                  onCheckedChange={(v) => void onToggleNotify(s, v === true)}
                />
                Emails
              </label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 gap-1 px-2 text-muted-foreground hover:text-destructive"
                onClick={() => void onRevoke(s)}
                disabled={revokingId === s.id}
                aria-label={`Remove ${s.shared_with_email}`}
              >
                {revokingId === s.id ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Trash2 className="h-3.5 w-3.5" />
                )}
              </Button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
